import { data, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import MainInfoTab from "./tabs/MainInfoTab";
import PageLoader from "../../components/pageLoader";
import { getEventById, updateEvent } from "../../api/eventsApi";
import { PencilIcon } from "@heroicons/react/outline";
import TicketsTablePage from "./tabs/TicketsPage";
import MarketingTab from "./tabs/marketing/MarketingTab";
import VouchersTab from "./tabs/vouchers/VouchersTab";
import InvitationsTab from "./tabs/invitations/invitationsTab";
import AffiliateTab from "./tabs/affiliates/AffiliateTab";
import SaleTagsTab from "./tabs/salesTags/SaleTagsTab";
import DashboardTab from "./tabs/dashboard/DashboardTab";
import SettlementsPage from "./tabs/settlements/SettlementsTab";

const TABS = [
  "Dashboard",
  "Main Info",
  "Tickets/ Tables",
  "Marketing",
  "Affiliates/ Affiliate Links",
  "Financial",
  "Invitations",
  "Vouchers / Discounts",
  "Settlements",
];

const EventDetailPage = () => {
  const { id } = useParams();

  const [activeTab, setActiveTab] = useState("Dashboard");

  const [event, setEvent] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");

  const [ticketsSubTab, setTicketsSubTab] = useState("tickets");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [toast, setToast] = useState(null);

  const navigate = useNavigate();

  const fetchEvents = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError("");
      const data = await getEventById(id);
      console.log('--', data);
      setEvent(data || []);
    } catch (err) {
      setError("Failed to load events.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleEditStatus = async () => {
    try {
      setLoading(true);
      await updateEvent(id, { status: selectedStatus });
      fetchEvents();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
    setShowStatusModal(false);
  };

  const handleUpdateMainInfo = async (form) => {
    try {
      setLoading(true);

      // const payload = {
      //   name: form.name,
      //   // pre_register_landing_page: form.link,
      //   distribution_channels: form.distribution_channels,
      //   category: form.category,
      //   date_start: form.date_start,
      //   date_end: form.date_end,
      //   client_commission_percentage: Number(form.client_commission_percentage),
      //   can_buy_without_login: form.can_buy_without_login,
      //   meta_tags: form.meta_tags,
      //   //flyer_horizontal: form.flyer_horizontal,
      //   //flyer_portrait: form.flyer_portrait,
      // };

      const payload = new FormData();

      // basic fields
      payload.append("name", form.name);
      payload.append("category", form.category);
      payload.append("date_start", form.date_start);
      payload.append("date_end", form.date_end);
      payload.append(
        "client_commission_percentage",
        form.client_commission_percentage,
      );
      payload.append("commission_vat_included", form.commission_vat_included);
      payload.append(
        "commission_vat_percentage",
        form.commission_vat_percentage,
      );
      payload.append("currency", form.currency);
      payload.append("can_buy_without_login", form.can_buy_without_login);

      // arrays (IMPORTANT)
      payload.append(
        "distribution_channels",
        JSON.stringify(form.distribution_channels),
      );

      payload.append("meta_tags", JSON.stringify(form.meta_tags));

      payload.append("map_address", form.map_address);
      payload.append("map_latitude", form.map_latitude);
      payload.append("map_longitude", form.map_longitude);

      // form.venue_name && payload.append("venue.name", form.venue_name);
      // form.venue_addressLine1 && payload.append("venue.address.address_1", form.venue_addressLine1);
      // form.venue_addressLine2 && payload.append("venue.address.address_2", form.venue_addressLine2);
      // form.venue_addressLine3 && payload.append("venue.address.address_3", form.venue_addressLine3);
      // form.venue_city && payload.append("venue.address.city", form.venue_city);
      // form.venue_county && payload.append("venue.address.county", form.venue_county);
      // form.venue_country && payload.append("venue.address.country", form.venue_country);
      // form.venue_postcode && payload.append("venue.address.postcode", form.venue_postcode);

      payload.append("event_specific_type", form.event_specific_type);
      payload.append("dress_code", form.dress_code);

      if (form.updated_flyer_horizontal) {
        payload.append("flyer_horizontal", form.flyer_horizontal);
      }

      if (form.updated_flyer_portrait) {
        payload.append("flyer_portrait", form.flyer_portrait);
      }
      form.artists.length > 0 &&
        (await updateEvent(id, { event_artists: form.artists }));

      const updated = await updateEvent(id, payload);

      setEvent(updated);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to update event",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDescription = async (description) => {
    try {
      setLoading(true);

      const updated = await updateEvent(id, {
        description,
      });
      setEvent(updated);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to update description",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrganiser = async (info) => {
    try {
      setLoading(true);

      const payload = new FormData();

      payload.append("show_organiser_details", info.show_organiser_details);
      payload.append("organiser_name", info.organiser_name);
      payload.append("organiser_company_name", info.organiser_company_name);
      payload.append("organiser_company_reg_no", info.organiser_company_reg_no);
      payload.append("organiser_vat_no", info.organiser_vat_no);

      if (info.organiser_logo) {
        payload.append("organiser_logo", info.organiser_logo);
      }

      const updated = await updateEvent(id, payload);
      setEvent(updated);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to update organiser details",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "published":
        return "bg-green-500/10 text-green-400";
      case "draft":
        return "bg-yellow-500/10 text-yellow-400";
      case "cancelled":
        return "bg-red-500/10 text-red-400";
      case "completed":
        return "bg-pink-500/10 text-pink-400";
      default:
        return "bg-slate-700 text-slate-300";
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-400 hover:text-white"
        >
          ← Back to Events
        </button>
      </div>
      {/* HEADER */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl p-3">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-xl font-semibold text-white">{event.name}</h1>

            <p className="text-sm text-slate-400 mt-1">
              {`${formatDate(event.date_start)} → ${formatDate(event.date_end)}`}
            </p>

            <p className="text-sm text-slate-400">{event.company?.name}</p>

            <div>
              <a
                href={event.public_page_url}
                target="_blank"
                rel="noreferrer"
                className="text-pink-500 hover:underline"
              >
                View Event
              </a>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div
              className={`px-4 py-2 rounded-md text-m ${getStatusStyle(event.status)}`}
            >
              {event.status}
            </div>

            <button
              onClick={() => {
                setSelectedStatus(event.status);
                setShowStatusModal(true);
              }}
              className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-slate-700 transition"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-2 border-b border-white/10">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm rounded-t-lg transition ${
              activeTab === tab
                ? "bg-slate-900 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div className="p-3">
        {activeTab === "Dashboard" && <DashboardTab event={event} />}
        {activeTab === "Main Info" && (
          <MainInfoTab
            data={event}
            onUpdateMain={handleUpdateMainInfo}
            onUpdateDescription={handleUpdateDescription}
            onUpdateOrganiser={handleUpdateOrganiser}
            loading={loading}
          />
        )}
        {activeTab === "Tickets/ Tables" && (
          <TicketsTablePage
            event={event}
            onRefreshEvent={() => fetchEvents(true)}
            activeSubTab={ticketsSubTab}
            onSubTabChange={setTicketsSubTab}
          />
        )}
        {activeTab === "Marketing" && (
          <MarketingTab
            data={event}
            eventId={id}
            onUpdate={(updatedEvent) => setEvent(updatedEvent)}
          />
        )}
        {activeTab === "Affiliates/ Affiliate Links" && (
          <AffiliateTab eventId={id} />
        )}
        {activeTab === "Financial" && <div>Coming soon</div>}
        {activeTab === "Invitations" && <InvitationsTab eventId={id} />}
        {activeTab === "Vouchers / Discounts" && <VouchersTab eventId={id} />}
        {activeTab === "Settlements" && <div>Coming soon</div>}
      </div>

      {showStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="w-full max-w-md bg-slate-900 border border-white/10 rounded-xl shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10">
              <h2 className="text-white font-semibold">Update Status</h2>
            </div>

            {/* Body */}
            <div className="relative">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full appearance-none bg-[#131C31] px-4 pr-10 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              >
                <option value="draft">Draft</option>
                <option value="published">Publish</option>
                <option value="cancelled">Cancel</option>
                <option value="completed">Complete</option>
              </select>

              {/* Custom Arrow */}
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                ▼
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
              >
                Cancel
              </button>

              <button
                onClick={handleEditStatus}
                className="px-4 py-2 text-sm rounded-lg text-white bg-pink-600 hover:bg-pink-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailPage;
